import { AdminRouteItem } from 'hooks/adminRoutes/adminRoutes.api';
import { DEFAULT_ROUTE_SECTION } from 'constants/adminRoutes';
import { NavScreenGroup, NavScreenOption } from 'hooks/nav/useAccessibleNavScreens';
import { ScreenRouteEntry } from 'types/screenRoutes';

/**
 * Namespace for the `pageId` an admin-created route carries.
 *
 * `resolveRouteElement` prefers a mapped `pageId` over `iframe`, so an admin route whose id
 * happened to collide with a key in `routeComponentMapping` would render that screen instead of
 * the embedded page. The prefix guarantees the lookup misses without shipping a reserved-word
 * list to the backend.
 */
export const ADMIN_ROUTE_PAGE_ID_PREFIX = 'adminRoute:';

export const toAdminRoutePageId = (routeId: string): string => `${ADMIN_ROUTE_PAGE_ID_PREFIX}${routeId}`;

/** Config paths are stored bare; a stray leading slash would register `//x`. */
const toBarePath = (routePath: string): string => String(routePath || '').replace(/^\/+/, '');

const isUsableRoute = (route?: AdminRouteItem | null): boolean =>
    Boolean(
        route?.route_id &&
            route?.label &&
            toBarePath(route?.route_path) &&
            route?.iframe_url &&
            (route.level === 'parent' || route.level === 'sub'),
    );

/**
 * The router registry entry.
 *
 * `hideGlobalFilter` is coerced to a definite boolean: App.tsx reads it with `??`, where an
 * `undefined` hands the decision back to `ROUTES_WITHOUT_GLOBAL_FILTER` and a `false` forces the
 * ribbon on. Neither is a safe stand-in for the other.
 */
const toRouteEntry = (route: AdminRouteItem): ScreenRouteEntry => ({
    label: route.label,
    path: toBarePath(route.route_path),
    id: route.route_id,
    pageId: toAdminRoutePageId(route.route_id),
    iframe: { url: route.iframe_url, hideGlobalFilter: route.hide_global_filter === true },
});

/**
 * The nav option.
 *
 * `isIframe` is what exempts the option from the `userAccessScope` whitelist — an admin-created
 * route has no id in anybody's access scope, so without the flag it disappears for every user
 * whose scope is non-empty.
 */
const toNavOption = (route: AdminRouteItem): NavScreenOption => ({
    label: route.label,
    path: toBarePath(route.route_path),
    id: route.route_id,
    isIframe: true,
});

/** A top-level group with no children yet. `url` makes it navigable on its own. */
const toParentGroup = (route: AdminRouteItem): NavScreenGroup => ({
    label: route.label,
    path: [toBarePath(route.route_path)],
    id: route.route_id,
    description: route.description || undefined,
    // Admin groups name their column internally, so never surface it as a sub-header.
    showSubHeader: false,
    options: {},
    url: `/${toBarePath(route.route_path)}`,
});

/** Copies a group and its option arrays so the base config is never written through. */
const cloneGroup = (group: NavScreenGroup): NavScreenGroup => {
    const options: Record<string, any> = {};
    Object.keys(group?.options || {}).forEach((key) => {
        const entries = (group.options as Record<string, any>)[key];
        options[key] = Array.isArray(entries) ? [...entries] : entries;
    });
    return { ...group, options };
};

const attachParentGroup = (
    route: AdminRouteItem,
    groups: NavScreenGroup[],
    groupsById: Map<string, NavScreenGroup>,
): boolean => {
    // A group id colliding with a native one would shadow that whole section of the nav.
    if (groupsById.has(route.route_id)) return false;
    const group = toParentGroup(route);
    groups.push(group);
    groupsById.set(route.route_id, group);
    return true;
};

/**
 * Appends the option to `parent_id` / `section`.
 *
 * A missing parent means this tenant does not have that section — routes are tenant-scoped but
 * configs are not identical, so the row is skipped rather than force-creating a group nobody
 * curated. A missing *column* is created instead of skipped: that is the case for a top-level
 * admin route being given its first child, which starts life with no columns at all.
 */
const attachSubOption = (route: AdminRouteItem, groupsById: Map<string, NavScreenGroup>): boolean => {
    const group = groupsById.get(route.parent_id || '');
    if (!group) return false;

    const section = route.section || DEFAULT_ROUTE_SECTION;
    const options = group.options as Record<string, any>;
    if (!Array.isArray(options[section])) options[section] = [];
    options[section].push(toNavOption(route));
    return true;
};

/**
 * Folds the admin-managed route list into the tenant config the app already has.
 *
 * Native screens win outright: a row whose `route_id` or `route_path` is already taken by
 * `screenDetailsWithRoutes` is dropped, so a bad database row can never shadow a real screen.
 * Rows are applied in the order the API returns them, which is why a parent created earlier can
 * be the parent of a later subroute.
 *
 * Pure and non-mutating — `baseConfig` and everything reachable from it come back untouched, and
 * a null base passes straight through so `ConfigContext`'s spinner still gates on it.
 */
export function mergeAdminRoutes(baseConfig: any, routes?: AdminRouteItem[] | null): any {
    if (!baseConfig) return baseConfig;

    const usableRoutes = (Array.isArray(routes) ? routes : []).filter(isUsableRoute);
    if (!usableRoutes.length) return baseConfig;

    const screenDetails: any[] = Array.isArray(baseConfig.screenDetailsWithRoutes)
        ? baseConfig.screenDetailsWithRoutes
        : [];
    const screens: NavScreenGroup[] = Array.isArray(baseConfig.screens) ? baseConfig.screens : [];

    const takenIds = new Set(screenDetails.map((entry) => entry?.id).filter(Boolean));
    const takenPaths = new Set(screenDetails.map((entry) => entry?.path).filter(Boolean));

    const nextScreenDetails = [...screenDetails];
    const nextScreens = screens.map(cloneGroup);
    const groupsById = new Map<string, NavScreenGroup>(
        nextScreens.filter((group) => group?.id).map((group) => [group.id as string, group]),
    );

    usableRoutes.forEach((route) => {
        const path = toBarePath(route.route_path);
        if (takenIds.has(route.route_id) || takenPaths.has(path)) return;

        const attached =
            route.level === 'parent'
                ? attachParentGroup(route, nextScreens, groupsById)
                : attachSubOption(route, groupsById);
        if (!attached) return;

        nextScreenDetails.push(toRouteEntry(route));
        takenIds.add(route.route_id);
        takenPaths.add(path);
    });

    return { ...baseConfig, screenDetailsWithRoutes: nextScreenDetails, screens: nextScreens };
}

export default mergeAdminRoutes;
